
import { supabase } from '@/lib/supabase';
import { CategoryMarkupCalculation, PricingParams } from '@/types/line-item-pricing';

/**
 * Calculate the markup and final price for a line item based on its category
 */
export async function calculateCategoryPricing(
  params: PricingParams
): Promise<CategoryMarkupCalculation> {
  const { categoryId, amount, companyId } = params;
  
  if (!categoryId || !companyId) {
    // Return default values if required parameters are missing
    return {
      basePrice: amount,
      markupPercentage: 0,
      marginPercentage: 0,
      finalPrice: amount
    };
  }
  
  try {
    // Get markup percentage for this category and amount
    const { data: markupData, error: markupError } = await supabase
      .rpc('get_category_markup', { 
        p_category_id: categoryId,
        p_amount: amount,
        p_company_id: companyId
      });
      
    if (markupError) throw markupError;
    
    // Get margin percentage for this category and amount
    const { data: marginData, error: marginError } = await supabase
      .rpc('get_category_margin', { 
        p_category_id: categoryId,
        p_amount: amount,
        p_company_id: companyId
      });
      
    if (marginError) throw marginError;
    
    const markupPercentage = markupData || 0;
    const marginPercentage = marginData || 0;
    const finalPrice = amount * (1 + markupPercentage / 100);
    
    return {
      basePrice: amount,
      markupPercentage,
      marginPercentage,
      finalPrice
    };
    
  } catch (error) {
    console.error("Error calculating category pricing:", error);
    
    // Return default values in case of error
    return {
      basePrice: amount,
      markupPercentage: 0,
      marginPercentage: 0,
      finalPrice: amount
    };
  }
}

/**
 * Calculate margin percentage from markup percentage
 */
export function calculateMarginFromMarkup(markupPercentage: number): number {
  return parseFloat(((markupPercentage / (100 + markupPercentage)) * 100).toFixed(2));
}

/**
 * Calculate markup percentage from margin percentage
 */
export function calculateMarkupFromMargin(marginPercentage: number): number {
  if (marginPercentage >= 100) return 0; // Avoid division by zero or negative values
  return parseFloat(((marginPercentage / (100 - marginPercentage)) * 100).toFixed(2));
}

/**
 * Apply markup to a base price
 */
export function applyMarkup(basePrice: number, markupPercentage: number): number {
  return basePrice * (1 + markupPercentage / 100);
}

/**
 * Get base price from a price that already includes margin
 */
export function getBaseFromMargin(finalPrice: number, marginPercentage: number): number {
  if (marginPercentage >= 100) return finalPrice; // Avoid division by zero or negative values
  return finalPrice * (1 - marginPercentage / 100);
}
